// ---------------------------------------------------------------------------
// InputsV3GlobalTopBar — JSS.
// ---------------------------------------------------------------------------
// Geometria: 800x48, "encaixada" no topo da viewport (border-radius
// apenas nas pontas inferiores, casando com o padrao da
// ``monthlyStatus`` antiga). Tres blocos horizontais:
//
//   [ lead | prose (flex) | stats (3 tiles) | actions ]
//
// Estilo da Variante D do sandbox (``sandbox/inputs_v3_topbar_variants.html``):
//   - cluster ``stats`` com fundo cinza claro e bordas SOLIDAS entre
//     tiles (sensacao mais "tabular");
//   - tiles em duas linhas (numero em cima, label em caps);
//   - prose com icone inline + frase unica.
// ---------------------------------------------------------------------------

import { makeStyles } from "@material-ui/core/styles";

// Paleta canonica do projeto (``material-dashboard-pro-react.js``).
// Usamos hex direto p/ os tons especificos da topbar para evitar
// dependencia transitiva em ``successColor[N]`` / ``infoColor[N]``
// que mudam quando o esquema do template e atualizado.
const INK = "#2f3b41";
// ``INK_NUM``: tom levemente mais claro que ``INK`` p/ os numeros
// dos tiles (pedido do usuario: "um cinza um pouco mais claro nos
// numeros"). Mantemos ``INK`` puro para labels/headline para nao
// roubar peso visual de la.
const INK_NUM = "#4a5560";
const INK_SOFT = "#5b6b73";
const GRAY_BG = "#f7f8fa";
const GRAY_BORDER = "#eceff1";
const GRAY_DOT = "#cccccc";
const GRAY_DOT_DARK = "#bbbbbb";
const INFO = "#00acc1"; // alinhado ao ``infoColor[0]`` do tema atual (uso pontual)
const WARN = "#fb8c00";
const WARN_HALO = "rgba(251, 140, 0, 0.45)";
const DANGER = "#e53935";
// Verde "modeling-em-curso": o usuario pediu (mai/2026) que o estado
// ``processing`` da topbar usasse VERDE — em vez do turquesa info.
// Mantemos um tom levemente mais "vivo" que ``successColor[2]``
// (#43a047) para diferenciar do estado ``ready`` (parado verde).
const SUCCESS = "#43a047";
const PROCESSING_HALO = "rgba(67, 160, 71, 0.45)";

const useStyles = makeStyles((theme) => ({
  "@keyframes inputsV3TopBarDotPulse": {
    "0%": { boxShadow: "0 0 0 0 " + PROCESSING_HALO },
    "70%": { boxShadow: "0 0 0 6px rgba(67, 160, 71, 0)" },
    "100%": { boxShadow: "0 0 0 0 rgba(67, 160, 71, 0)" },
  },
  "@keyframes inputsV3TopBarDotPulseWarn": {
    "0%": { boxShadow: "0 0 0 0 " + WARN_HALO },
    "70%": { boxShadow: "0 0 0 6px rgba(251, 140, 0, 0)" },
    "100%": { boxShadow: "0 0 0 0 rgba(251, 140, 0, 0)" },
  },
  "@keyframes inputsV3TopBarSpin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
  bar: {
    position: "relative",
    display: "flex",
    alignItems: "stretch",
    width: 800,
    height: 38,
    background: "#ffffff",
    border: "1px solid " + GRAY_BORDER,
    borderTop: "none",
    borderRadius: "0 0 8px 8px",
    overflow: "hidden",
    fontFamily: theme?.typography?.fontFamily,
  },

  // ===== Overlay de upload (drag & drop / envio em curso) ===================
  // Cobre toda a barra enquanto um arquivo esta sendo enviado: nome
  // legivel do input + barra de progresso fina (mesmo "DNA" visual da
  // barra do drawer — trilho cinza claro, preenchimento na cor do
  // cliente). Ao terminar o envio, o overlay desaparece e a barra
  // volta ao estado natural.
  uploadOverlay: {
    position: "absolute",
    inset: 0,
    zIndex: 6,
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "0 16px",
    borderRadius: "0 0 8px 8px",
  },
  uploadIcon: {
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    color: SUCCESS,
    "& svg": { width: 18, height: 18 },
    animation: "$inputsV3TopBarUploadFloat 1.4s ease-in-out infinite",
  },
  "@keyframes inputsV3TopBarUploadFloat": {
    "0%": { transform: "translateY(0)" },
    "50%": { transform: "translateY(-2px)" },
    "100%": { transform: "translateY(0)" },
  },
  uploadText: {
    flex: 1,
    minWidth: 0,
    fontSize: 13,
    color: INK_SOFT,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    "& strong": { color: INK, fontWeight: 600 },
  },
  uploadPct: {
    flexShrink: 0,
    fontSize: 12,
    color: INK_SOFT,
    fontVariantNumeric: "tabular-nums",
  },
  uploadTrack: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 3,
    background: "#eeeeee",
  },
  uploadFill: {
    height: "100%",
    transition: "width 120ms ease-out",
  },

  // ===== Lead (dot + mes) ===================================================
  lead: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    // -1px nos paddings laterais (pedido do usuario): mais ar para
    // o texto central que pode ficar longo.
    padding: "0 13px 0 15px",
    whiteSpace: "nowrap",
    borderRight: "1px solid " + GRAY_BORDER,
    flexShrink: 0,
  },
  dot: {
    width: 9,
    height: 9,
    borderRadius: "50%",
    display: "inline-block",
    background: GRAY_DOT,
    flexShrink: 0,
  },
  dotProcessing: {
    // Verde "modeling-em-curso" (decisao mai/2026): cor unica para
    // qualquer estado ativo da modelagem, sem distincao info/turquoise.
    background: SUCCESS,
    animation: "$inputsV3TopBarDotPulse 1.6s ease-out infinite",
  },
  dotAction: {
    background: WARN,
    animation: "$inputsV3TopBarDotPulseWarn 1.6s ease-out infinite",
  },
  dotReady: { background: SUCCESS },
  dotWaiting: { background: GRAY_DOT },
  month: {
    fontSize: 13,
    fontWeight: 600,
    color: INK,
    "& em": {
      fontStyle: "normal",
      color: INK_SOFT,
      fontWeight: 400,
    },
  },

  // ===== Prose (frase unica do estado) =====================================
  prose: {
    flex: 1,
    minWidth: 0,
    fontSize: 13,
    color: INK_SOFT,
    display: "flex",
    alignItems: "center",
    padding: "0 13px",
  },
  proseWrap: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",
    width: "100%",
  },
  proseLbl: { color: INK, fontWeight: 600 },
  // ``proseAccentInfo`` mantido como classe para minimizar churn,
  // mas hoje renderiza no MESMO verde que ``proseAccentOk`` —
  // pedido do usuario para padronizar "processing" no verde.
  proseAccentInfo: { color: SUCCESS, fontWeight: 600 },
  proseAccentWarn: { color: WARN, fontWeight: 600 },
  proseAccentDanger: { color: DANGER, fontWeight: 600 },
  proseAccentOk: { color: SUCCESS, fontWeight: 600 },
  proseEm: { color: INK_SOFT, fontWeight: 400, fontStyle: "normal" },
  proseIcon: {
    width: 16,
    height: 16,
    marginRight: 6,
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    "& svg": { width: 16, height: 16 },
  },
  proseIconProcessing: {
    color: SUCCESS,
    animation: "$inputsV3TopBarSpin 1.6s linear infinite",
  },
  proseIconAction: { color: WARN },
  proseIconReady: { color: SUCCESS },
  proseIconWaiting: { color: GRAY_DOT_DARK },

  // ===== Stats (3 tiles clicaveis) =========================================
  stats: {
    display: "flex",
    alignItems: "stretch",
    flexShrink: 0,
    background: GRAY_BG,
    borderLeft: "1px solid " + GRAY_BORDER,
    borderRight: "1px solid " + GRAY_BORDER,
  },
  tile: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 10px",
    height: "100%",
    textAlign: "center",
    lineHeight: 1.1,
    border: "none",
    background: "transparent",
    cursor: "default",
    transition: "background 120ms",
    // ``font: inherit`` + ``color: inherit`` — sem isso, quando o
    // tile e' renderizado como ``<button>`` (variante clicavel),
    // o user-agent injeta font-family/size/color proprios e o tile
    // fica visualmente diferente do tile nao-clicavel (renderizado
    // como ``<div>``). Foi o que o usuario apontou ao observar que
    // "Entregues" parecia ter formatacao diferente das outras duas
    // labels — na verdade, era a versao certa, e as outras 2 que
    // estavam fora do padrao.
    font: "inherit",
    color: "inherit",
    "&:focus": { outline: "none" },
    "&:focus-visible": {
      outline: "2px solid " + INFO,
      outlineOffset: -2,
    },
  },
  tileClickable: {
    cursor: "pointer",
    "&:hover": { background: "#eef1f4" },
  },
  tileDivider: { borderLeft: "1px solid " + GRAY_BORDER },
  tileN: {
    fontSize: 14,
    fontWeight: 600,
    color: INK_NUM,
  },
  tileNWarn: { color: WARN },
  tileNDanger: { color: DANGER },
  tileK: {
    fontSize: 9,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: INK_SOFT,
    marginTop: 2,
  },

  // ===== Actions (apenas refresh) ==========================================
  actions: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    padding: "0 4px",
    flexShrink: 0,
  },
  iconBtn: {
    width: 32,
    height: 32,
    border: "none",
    background: "transparent",
    color: INK_SOFT,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 6,
    transition: "background 120ms, color 120ms",
    "&:hover:not(:disabled)": { background: GRAY_BG, color: INK },
    "&:disabled": {
      color: "#b0bec5",
      cursor: "not-allowed",
    },
    "&:focus": { outline: "none" },
    "&:focus-visible": {
      outline: "2px solid " + INFO,
      outlineOffset: -2,
    },
    "& svg": { width: 18, height: 18 },
  },
  // Wrapper que troca o icone do refresh pelo ``ThreeDots`` durante
  // o fetch. Largura/altura FIXAS para evitar "saltinho" lateral
  // quando o icone vira spinner (pedido do usuario).
  refreshSlot: {
    width: 18,
    height: 18,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  refreshDotsWrap: {
    width: 18,
    height: 18,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  // Fade aplicado nos numeros dos tiles durante a transicao entre
  // meses (alinhado com a faixa de progresso da ``ModelingProgressBandV3``).
  numbersFade: {
    opacity: 0.45,
    transition: "opacity 180ms ease",
  },
  numbersIdle: {
    opacity: 1,
    transition: "opacity 180ms ease",
  },
}));

export default useStyles;
